import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ImageUploader from '../components/ImageUploader';
import SpecialtyInput from '../components/SpecialtyInput';
import CurrencyPicker from '../components/CurrencyPicker';
import PayoutSettings from '../components/PayoutSettings';
import { resolveImageUrl } from '../utils/imageUrl';
import { User, Mail, Calendar, Edit2, Save, X, MapPin, Clock, Eye, EyeOff, Camera, MailX } from 'lucide-react';
import { splitSpecialties } from '../utils/specialty';
import { READINESS_CATEGORIES } from '../utils/readiness';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AGE_RANGES = ['18-30', '31-45', '46-65', '65+'];

export default function ProfilePage() {
    const { user, refreshUser } = useAuth();
    const [editing, setEditing] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        bio: '',
        country: '',
        city: '',
        age_range: '',
        is_discoverable: true,
        profile_image_url: '',
        specialty: '',
        website: '',
        readiness_categories: [],
        email_connection_notifications: true,
        library_access_notify: 'off'
    });
    const [saving, setSaving] = useState(false);
    const isPillar = user?.membership_tier === 'partnership';
    const canMultiCategory = user?.role === 'admin' || !!user?.can_multi_category;

    useEffect(() => {
        if (user) {
            setFormData({
                name: user.name || '',
                bio: user.bio || '',
                country: user.country || '',
                city: user.city || '',
                age_range: user.age_range || '',
                is_discoverable: user.is_discoverable !== false,
                profile_image_url: user.profile_image_url || '',
                specialty: user.specialty || '',
                website: user.website || '',
                readiness_categories: Array.isArray(user.readiness_categories) ? user.readiness_categories : [],
                email_connection_notifications: user.email_connection_notifications !== false,
                library_access_notify: user.library_access_notify || 'off'
            });
        }
    }, [user]);

    const toggleReadiness = (key) => {
        setFormData((f) => {
            const current = f.readiness_categories || [];
            const has = current.includes(key);
            if (canMultiCategory) {
                return { ...f, readiness_categories: has ? current.filter(c => c !== key) : [...current, key] };
            }
            // Single-select: picking the same key clears it, picking a new one replaces.
            return { ...f, readiness_categories: has ? [] : [key] };
        });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.put(`${API_URL}/api/profile`, formData, { withCredentials: true });
            toast.success('Profile updated successfully');
            await refreshUser();
            setEditing(false);
        } catch (error) {
            toast.error('Failed to update profile');
        } finally {
            setSaving(false);
        }
    };

    const getMembershipBadgeClass = (tier) => {
        switch (tier) {
            case 'seeker': return 'tier-badge tier-seeker';
            case 'walker': return 'tier-badge tier-walker';
            case 'architect': return 'tier-badge tier-architect';
            case 'partnership': return 'tier-badge tier-partnership';
            default: return 'tier-badge bg-gray-100 text-gray-600';
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <div className="min-h-screen bg-[#F9F6F0]">
            <Navbar />

            <main className="pt-24 pb-16" data-testid="profile-page">
                <div className="max-w-3xl mx-auto px-6 sm:px-12">
                    {/* Header */}
                    <div className="mb-8">
                        <p className="overline mb-2">Your Account</p>
                        <h1 className="heading-2">Profile</h1>
                    </div>

                    {/* Profile Card */}
                    <div className="bg-white border border-[#E5E0D8] rounded-sm overflow-hidden" data-testid="profile-card">
                        {/* Header Banner */}
                        <div className="h-32 bg-gradient-to-r from-[#5B2D8E] to-[#D8B4E2] relative"></div>

                        {/* Profile Content */}
                        <div className="px-8 pb-8">
                            {/* Avatar */}
                            <div className="relative -mt-16 mb-6">
                                {user?.profile_image_url || formData.profile_image_url ? (
                                    <img 
                                        src={editing ? resolveImageUrl(formData.profile_image_url) : resolveImageUrl(user?.profile_image_url)}
                                        alt={user?.name}
                                        className="w-32 h-32 rounded-full border-4 border-white object-cover shadow-lg"
                                    />
                                ) : (
                                    <div className="w-32 h-32 bg-[#5B2D8E] rounded-full border-4 border-white flex items-center justify-center text-white font-serif text-4xl shadow-lg">
                                        {user?.name?.charAt(0) || 'U'}
                                    </div>
                                )}
                                {editing && (
                                    <div className="absolute bottom-0 right-0 bg-white rounded-full p-2 shadow-md">
                                        <Camera className="w-5 h-5 text-[#5B2D8E]" />
                                    </div>
                                )}
                            </div>

                            {/* Edit Button */}
                            {!editing && (
                                <button
                                    onClick={() => setEditing(true)}
                                    className="absolute top-48 right-8 btn-secondary text-xs px-4 py-2 flex items-center gap-2"
                                    data-testid="edit-profile-btn"
                                >
                                    <Edit2 className="w-4 h-4" />
                                    Edit Profile
                                </button>
                            )}

                            {editing ? (
                                <div className="space-y-6" data-testid="edit-form">
                                    {/* Profile Image Upload */}
                                    <ImageUploader
                                        value={formData.profile_image_url}
                                        onChange={(url) => setFormData({ ...formData, profile_image_url: url })}
                                        testIdPrefix="profile-image"
                                        label="Profile Image"
                                        aspect="square"
                                    />

                                    <div>
                                        <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Name</label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            className="w-full"
                                            data-testid="edit-name"
                                        />
                                    </div>

                                    <div>
                                        <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Bio</label>
                                        <textarea
                                            value={formData.bio}
                                            onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                                            rows={4}
                                            className="w-full resize-none"
                                            placeholder="Tell us about yourself..."
                                            data-testid="edit-bio"
                                        />
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Country</label>
                                            <input
                                                type="text"
                                                value={formData.country}
                                                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                                                className="w-full"
                                                placeholder="e.g. Belgium"
                                                data-testid="edit-country"
                                            />
                                        </div>
                                        <div>
                                            <label className="block font-sans text-sm text-[#1A1A2E] mb-2">City</label>
                                            <input
                                                type="text"
                                                value={formData.city}
                                                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                                                className="w-full"
                                                placeholder="e.g. Brussels"
                                                data-testid="edit-city"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Age Range</label>
                                        <select
                                            value={formData.age_range}
                                            onChange={(e) => setFormData({ ...formData, age_range: e.target.value })}
                                            className="w-full bg-white border border-[#E5E0D8] px-4 py-3 rounded-sm"
                                            data-testid="edit-age"
                                        >
                                            <option value="">Prefer not to say</option>
                                            {AGE_RANGES.map(range => (
                                                <option key={range} value={range}>{range}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="flex items-center gap-3 p-4 bg-[#F9F6F0] rounded-sm">
                                        <input
                                            type="checkbox"
                                            id="is_discoverable"
                                            checked={formData.is_discoverable}
                                            onChange={(e) => setFormData({ ...formData, is_discoverable: e.target.checked })}
                                            className="w-5 h-5 accent-[#5B2D8E]"
                                            data-testid="edit-discoverable"
                                        />
                                        <label htmlFor="is_discoverable" className="flex-grow">
                                            <span className="font-sans text-sm text-[#1A1A2E]">Allow other members to find me</span>
                                            <p className="text-xs text-[#4A4A68]">When enabled, you'll appear in the member directory</p>
                                        </label>
                                        {formData.is_discoverable ? (
                                            <Eye className="w-5 h-5 text-[#5B2D8E]" />
                                        ) : (
                                            <EyeOff className="w-5 h-5 text-[#4A4A68]" />
                                        )}
                                    </div>

                                    <div className="flex items-center gap-3 p-4 bg-[#F9F6F0] rounded-sm">
                                        <input
                                            type="checkbox"
                                            id="email_connection_notifications"
                                            checked={formData.email_connection_notifications}
                                            onChange={(e) => setFormData({ ...formData, email_connection_notifications: e.target.checked })}
                                            className="w-5 h-5 accent-[#5B2D8E]"
                                            data-testid="edit-email-connection-notifications"
                                        />
                                        <label htmlFor="email_connection_notifications" className="flex-grow">
                                            <span className="font-sans text-sm text-[#1A1A2E]">Email me about connections &amp; messages</span>
                                            <p className="text-xs text-[#4A4A68]">New connection requests, accepted requests and new messages</p>
                                        </label>
                                        {formData.email_connection_notifications ? (
                                            <Mail className="w-5 h-5 text-[#5B2D8E]" />
                                        ) : (
                                            <MailX className="w-5 h-5 text-[#4A4A68]" />
                                        )}
                                    </div>

                                    {(user?.membership_tier === 'partnership' || user?.role === 'admin') && (
                                        <div className="p-4 bg-[#F9F6F0] rounded-sm flex items-center justify-between gap-4">
                                            <div>
                                                <span className="font-sans text-sm text-[#1A1A2E]">Email me when someone opens my library items</span>
                                                <p className="text-xs text-[#4A4A68]">When a member opens or downloads one of your guides or meditations</p>
                                            </div>
                                            <select
                                                value={formData.library_access_notify}
                                                onChange={(e) => setFormData({ ...formData, library_access_notify: e.target.value })}
                                                className="bg-white border border-[#E5E0D8] px-3 py-2 rounded-sm text-sm shrink-0"
                                                data-testid="edit-library-access-notify"
                                            >
                                                <option value="off">Never</option>
                                                <option value="first">First time per person</option>
                                                <option value="every">Every time</option>
                                            </select>
                                        </div>
                                    )}


                                    {/* Preferred display currency — synced across devices for logged-in users */}
                                    <div className="p-4 bg-[#F9F6F0] rounded-sm flex items-center justify-between gap-4">
                                        <div>
                                            <p className="font-sans text-sm text-[#1A1A2E]">Preferred currency</p>
                                            <p className="text-xs text-[#4A4A68]">All prices on the site are shown in this currency.</p>
                                        </div>
                                        <CurrencyPicker variant="profile" />
                                    </div>

                                    {isPillar && (
                                        <div className="p-6 border-l-4 border-[#5B2D8E] bg-[#5B2D8E]/5 rounded-sm space-y-4">
                                            <div>
                                                <p className="text-xs uppercase tracking-widest text-[#5B2D8E] mb-1">Mirror Circle Partner</p>
                                                <p className="text-sm text-[#4A4A68]">These two fields appear on your public pillar card at <code className="bg-white px-1">/pillars</code>.</p>
                                            </div>
                                            <div>
                                                <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Specialty</label>
                                                <SpecialtyInput
                                                    value={formData.specialty}
                                                    onChange={(next) => setFormData({ ...formData, specialty: next })}
                                                    placeholder="Mirror-Based Therapy, Breathwork, Reiki…"
                                                    testIdPrefix="edit-specialty"
                                                />
                                            </div>
                                            <div>
                                                <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Website</label>
                                                <input
                                                    type="url"
                                                    value={formData.website}
                                                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                                                    className="w-full"
                                                    placeholder="https://yoursite.com"
                                                    data-testid="edit-website"
                                                />
                                                <p className="text-xs text-[#4A4A68] mt-1">If you omit https:// we'll add it automatically.</p>
                                            </div>
                                            <div>
                                                <label className="block font-sans text-sm text-[#1A1A2E] mb-2">
                                                    Audience readiness
                                                    <span className="text-xs text-[#4A4A68] font-normal ml-2">
                                                        {canMultiCategory ? '(you can pick multiple)' : '(pick one)'}
                                                    </span>
                                                </label>
                                                <div className="space-y-2" data-testid="edit-readiness">
                                                    {READINESS_CATEGORIES.map(cat => {
                                                        const selected = (formData.readiness_categories || []).includes(cat.key);
                                                        return (
                                                            <button
                                                                key={cat.key}
                                                                type="button"
                                                                onClick={() => toggleReadiness(cat.key)}
                                                                className={`w-full text-left p-3 rounded-sm border transition-colors ${selected ? `${cat.badgeClass} border-current ring-1 ring-current` : 'bg-white border-[#E5E0D8] hover:bg-[#F9F6F0]'}`}
                                                                data-testid={`edit-readiness-${cat.key}`}
                                                            >
                                                                <div className="flex items-start gap-2">
                                                                    <span className={`inline-block w-2 h-2 rounded-full mt-1.5 ${cat.dotClass}`} />
                                                                    <div className="flex-1">
                                                                        <p className="text-sm font-medium">
                                                                            {cat.key} — {cat.title}
                                                                        </p>
                                                                        <p className="text-xs text-[#4A4A68] mt-0.5">{cat.subtitle}</p>
                                                                        <p className="text-xs text-[#4A4A68] mt-1">{cat.description}</p>
                                                                    </div>
                                                                </div>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Pillar payout settings — Stripe Connect / Invoice */}
                                    {isPillar && (
                                        <PayoutSettings user={user} onSaved={refreshUser} />
                                    )}

                                    <div className="flex gap-4">
                                        <button
                                            onClick={handleSave}
                                            className="btn-primary flex items-center gap-2"
                                            disabled={saving}
                                            data-testid="save-profile-btn"
                                        >
                                            <Save className="w-4 h-4" />
                                            {saving ? 'Saving...' : 'Save Changes'}
                                        </button>
                                        <button
                                            onClick={() => {
                                                setEditing(false);
                                                setFormData({
                                                    name: user?.name || '',
                                                    bio: user?.bio || '',
                                                    country: user?.country || '',
                                                    city: user?.city || '',
                                                    age_range: user?.age_range || '',
                                                    is_discoverable: user?.is_discoverable !== false,
                                                    profile_image_url: user?.profile_image_url || '',
                                                    specialty: user?.specialty || '',
                                                    website: user?.website || '',
                                                    readiness_categories: Array.isArray(user?.readiness_categories) ? user.readiness_categories : [],
                                                    email_connection_notifications: user?.email_connection_notifications !== false,
                                                    library_access_notify: user?.library_access_notify || 'off'
                                                });
                                            }}
                                            className="btn-secondary flex items-center gap-2"
                                            data-testid="cancel-edit-btn"
                                        >
                                            <X className="w-4 h-4" />
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {/* Name */}
                                    <div>
                                        <h2 className="font-serif text-3xl text-[#1A1A2E]" data-testid="profile-name">
                                            {user?.name}
                                        </h2>
                                        <div className="flex items-center gap-3 mt-2">
                                            {user?.membership_tier && (
                                                <span className={getMembershipBadgeClass(user.membership_tier)} data-testid="profile-tier">
                                                    {user.membership_tier}
                                                </span>
                                            )}
                                            {user?.role === 'admin' && (
                                                <span className="px-2 py-1 bg-[#5B2D8E] text-white text-xs rounded-sm">Admin</span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Bio */}
                                    {user?.bio && (
                                        <p className="font-sans text-[#4A4A68]" data-testid="profile-bio">
                                            {user.bio}
                                        </p>
                                    )}

                                    {/* Location */}
                                    {(user?.city || user?.country) && (
                                        <div className="flex items-center gap-2 text-[#4A4A68]">
                                            <MapPin className="w-5 h-5 text-[#5B2D8E]" />
                                            <span>
                                                {[user.city, user.country].filter(Boolean).join(', ')}
                                            </span>
                                        </div>
                                    )}

                                    {/* Pillar-only public fields */}
                                    {isPillar && (user?.specialty || user?.website) && (
                                        <div className="p-4 bg-[#5B2D8E]/5 border-l-4 border-[#5B2D8E] rounded-sm space-y-2">
                                            <p className="text-xs uppercase tracking-widest text-[#5B2D8E]">Public pillar card</p>
                                            {user?.specialty && (
                                                <div data-testid="profile-specialty">
                                                    <p className="text-sm text-[#1A1A2E] mb-1"><strong>Specialty:</strong></p>
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {splitSpecialties(user.specialty).map(tag => (
                                                            <span
                                                                key={tag}
                                                                className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-[#5B2D8E]/30 text-[#5B2D8E] rounded-sm"
                                                            >
                                                                {tag}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {user?.website && (
                                                <p className="text-sm text-[#1A1A2E]" data-testid="profile-website">
                                                    <strong>Website:</strong>{' '}
                                                    <a href={user.website} target="_blank" rel="noopener noreferrer" className="text-[#5B2D8E] hover:underline">
                                                        {user.website}
                                                    </a>
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    {/* Info Grid */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-[#E5E0D8]">
                                        <div className="flex items-center gap-3">
                                            <Mail className="w-5 h-5 text-[#5B2D8E]" />
                                            <div>
                                                <p className="font-sans text-xs text-[#4A4A68] uppercase tracking-wider">Email</p>
                                                <p className="font-sans text-[#1A1A2E]" data-testid="profile-email">{user?.email}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3">
                                            <Calendar className="w-5 h-5 text-[#5B2D8E]" />
                                            <div>
                                                <p className="font-sans text-xs text-[#4A4A68] uppercase tracking-wider">Member Since</p>
                                                <p className="font-sans text-[#1A1A2E]" data-testid="profile-joined">
                                                    {formatDate(user?.created_at)}
                                                </p>
                                            </div>
                                        </div>

                                        {user?.age_range && (
                                            <div className="flex items-center gap-3">
                                                <User className="w-5 h-5 text-[#5B2D8E]" />
                                                <div>
                                                    <p className="font-sans text-xs text-[#4A4A68] uppercase tracking-wider">Age Range</p>
                                                    <p className="font-sans text-[#1A1A2E]">{user.age_range}</p>
                                                </div>
                                            </div>
                                        )}

                                        {user?.membership_expires && (
                                            <div className="flex items-center gap-3">
                                                <Clock className="w-5 h-5 text-[#5B2D8E]" />
                                                <div>
                                                    <p className="font-sans text-xs text-[#4A4A68] uppercase tracking-wider">Membership Expires</p>
                                                    <p className="font-sans text-[#1A1A2E]" data-testid="profile-expires">
                                                        {formatDate(user?.membership_expires)}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Discoverability Status */}
                                    <div className="flex items-center gap-2 text-sm text-[#4A4A68]">
                                        {user?.is_discoverable !== false ? (
                                            <>
                                                <Eye className="w-4 h-4" />
                                                <span>Visible in member directory</span>
                                            </>
                                        ) : (
                                            <>
                                                <EyeOff className="w-4 h-4" />
                                                <span>Hidden from member directory</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Change Password */}
                    <ChangePasswordSection />

                    {/* Membership Management */}
                    <div className="mt-8 content-card" data-testid="membership-management">
                        <h3 className="heading-3 mb-4">Membership</h3>
                        {user?.membership_tier ? (
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-sans text-[#4A4A68]">
                                        Current plan: <span className="font-medium text-[#1A1A2E] uppercase">{user.membership_tier}</span>
                                    </p>
                                    {user.membership_expires && (
                                        <p className="font-sans text-sm text-[#4A4A68]">
                                            Expires: {formatDate(user.membership_expires)}
                                        </p>
                                    )}
                                </div>
                                <a href="/membership" className="btn-secondary text-xs" data-testid="manage-membership-btn">
                                    Manage Membership
                                </a>
                            </div>
                        ) : (
                            <div className="flex items-center justify-between">
                                <p className="font-sans text-[#4A4A68]">No active membership</p>
                                <a href="/membership" className="btn-primary text-xs" data-testid="get-membership-btn">
                                    Join the Circle
                                </a>
                            </div>
                        )}
                    </div>

                    {/* Admin Link */}
                    {user?.role === 'admin' && (
                        <div className="mt-8 content-card" data-testid="admin-section">
                            <h3 className="heading-3 mb-4">Administration</h3>
                            <p className="font-sans text-[#4A4A68] mb-4">
                                You have administrator access to manage users, events, and resources.
                            </p>
                            <a href="/admin" className="btn-primary text-xs" data-testid="admin-link">
                                Go to Admin Panel
                            </a>
                        </div>
                    )}
                </div>
            </main>

            <Footer />
        </div>
    );
}

function ChangePasswordSection() {
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [confirm, setConfirm] = useState('');
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (next.length < 8) return toast.error('New password must be at least 8 characters.');
        if (next !== confirm) return toast.error('New passwords do not match.');
        setSaving(true);
        try {
            await axios.post(
                `${API_URL}/api/auth/change-password`,
                { current_password: current, new_password: next },
                { withCredentials: true }
            );
            toast.success('Password updated');
            setCurrent(''); setNext(''); setConfirm(''); setOpen(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not update password');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mt-8 content-card" data-testid="change-password-section">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="heading-3 mb-1">Password</h3>
                    <p className="font-sans text-sm text-[#4A4A68]">Change your sign-in password.</p>
                </div>
                {!open && (
                    <button onClick={() => setOpen(true)} className="btn-secondary text-xs" data-testid="open-change-password">
                        Change password
                    </button>
                )}
            </div>
            {open && (
                <form onSubmit={submit} className="mt-6 space-y-4" data-testid="change-password-form">
                    <div>
                        <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Current password</label>
                        <input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} className="w-full" data-testid="cp-current" />
                    </div>
                    <div>
                        <label className="block font-sans text-sm text-[#1A1A2E] mb-2">New password</label>
                        <input type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} className="w-full" data-testid="cp-new" />
                    </div>
                    <div>
                        <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Confirm new password</label>
                        <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-full" data-testid="cp-confirm" />
                    </div>
                    <div className="flex gap-3">
                        <button type="submit" disabled={saving} className="btn-primary text-xs" data-testid="cp-save">
                            {saving ? 'Saving…' : 'Save new password'}
                        </button>
                        <button type="button" onClick={() => { setOpen(false); setCurrent(''); setNext(''); setConfirm(''); }} className="btn-secondary text-xs" data-testid="cp-cancel">
                            Cancel
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
}
